'use client';

import * as React from 'react';
import Image from 'next/image';
import NiceModal from '@ebay/nice-modal-react';
import { ImageIcon, XIcon } from 'lucide-react';

import { Badge } from '@workspace/ui/components/badge';
import { Button } from '@workspace/ui/components/button';
import { Card, CardContent } from '@workspace/ui/components/card';
import { cn } from '@workspace/ui/lib/utils';

import { FullscreenImageModal } from '~/components/generator/shared/fullscreen-image-modal';
import { productCategoryLabel } from '~/lib/labels';
import type { ProductDto } from '~/types/dtos/product-dto';

type ProductCardProps = {
  item: ProductDto;
  onSelect: (product: ProductDto) => void;
  isSelected: boolean;
  disabled?: boolean;
};

export function ProductCard({
  item: product,
  onSelect,
  isSelected,
  disabled
}: ProductCardProps): React.JSX.Element {
  const [currentImageIndex, setCurrentImageIndex] = React.useState(0);
  const [isHovered, setIsHovered] = React.useState(false);
  const intervalRef = React.useRef<NodeJS.Timeout | null>(null);

  const images = product?.images || [];
  const hasMultipleImages = images.length > 1;
  const currentImage = images[currentImageIndex];
  const imageUrl = currentImage?.url || null;

  React.useEffect(() => {
    if (isHovered && hasMultipleImages) {
      intervalRef.current = setInterval(() => {
        setCurrentImageIndex((prev) => (prev + 1) % images.length);
      }, 1500);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isHovered, hasMultipleImages, images.length]);

  const handleMouseLeave = () => {
    setIsHovered(false);
    setCurrentImageIndex(0);
  };

  return (
    <Card
      className={cn(
        'group h-48 w-full p-0 transition-all md:h-64 overflow-hidden',
        isSelected && 'border-2 border-primary',
        disabled
          ? 'cursor-not-allowed opacity-50'
          : 'cursor-pointer hover:shadow-md'
      )}
      onClick={disabled ? undefined : () => onSelect(product)}
      onMouseEnter={disabled ? undefined : () => setIsHovered(true)}
      onMouseLeave={disabled ? undefined : handleMouseLeave}
    >
      <CardContent className="h-full p-0">
        <div className="relative h-full w-full overflow-hidden rounded-lg">
          {imageUrl ? (
            <>
              <Image
                src={imageUrl}
                alt=""
                width={100}
                height={100}
                className="absolute inset-0 h-full w-full scale-110 object-cover blur-sm"
              />
              <Image
                src={imageUrl}
                alt={product.name}
                width={500}
                height={500}
                className="absolute inset-0 h-full w-full object-contain transition-transform group-hover:scale-105"
              />
            </>
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-muted">
              <span className="text-sm text-muted-foreground">No image</span>
            </div>
          )}

          {isHovered && hasMultipleImages && (
            <div className="absolute right-2 top-2 rounded bg-black/50 px-2 py-1 text-xs text-white">
              {currentImageIndex + 1} / {images.length}
            </div>
          )}

          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />

          <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
            <div className="space-y-2">
              <div>
                <h4 className="line-clamp-1 text-sm font-medium">
                  {product.name}
                </h4>
                {product.sku && (
                  <p className="text-xs text-white/80">SKU: {product.sku}</p>
                )}
              </div>
              <div className="flex items-center justify-between">
                <Badge
                  variant="secondary"
                  className="border-white/30 bg-white/20 text-xs text-white"
                >
                  {productCategoryLabel[product.category]}
                </Badge>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

type SelectedProductCardProps = {
  item: ProductDto;
  onRemove?: (productId: string) => void;
};

export function SelectedProductCard({
  item: product,
  onRemove
}: SelectedProductCardProps): React.JSX.Element {
  const imageUrl = product?.images?.[0]?.url || null;

  return (
    <Card
      className="group relative h-12 w-12 cursor-pointer p-0 transition-all hover:shadow-md md:h-18 md:w-18"
      onClick={() => {
        if (imageUrl) {
          NiceModal.show(FullscreenImageModal, {
            images: [
              {
                url: imageUrl,
                title: product.name,
                filename: `product-${new Date().toISOString().split('T')[0]}.jpg`
              }
            ],
            currentIndex: 0
          });
        }
      }}
    >
      <CardContent className="h-full p-0">
        <div className="relative h-full w-full overflow-hidden rounded-lg">
          {imageUrl ? (
            <>
              <Image
                src={imageUrl}
                alt="background duplicate image"
                width={72}
                height={72}
                className="absolute inset-0 h-full w-full scale-110 object-cover blur-sm"
              />
              <Image
                src={imageUrl}
                alt={product.name}
                width={72}
                height={72}
                className="absolute inset-0 h-full w-full object-contain transition-transform group-hover:scale-105"
              />
            </>
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-muted">
              <span className="text-sm text-muted-foreground">
                <ImageIcon className="h-4 w-4" />
              </span>
            </div>
          )}

          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
        </div>
      </CardContent>
      {onRemove && (
        <div className="absolute right-0 top-0 opacity-0 transition-opacity group-hover:opacity-100 sm:right-1 sm:top-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.stopPropagation();
              onRemove(product.id);
            }}
            className="h-12 w-12 bg-background/80 p-0 hover:bg-background hover:text-destructive sm:h-6 sm:w-6"
            title="Remove product"
          >
            <XIcon className="h-4 w-4" />
          </Button>
        </div>
      )}
    </Card>
  );
}
